import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export type PinOutcome = 
  | "not_knocked" | "not_home" | "not_interested" 
  | "callback" | "appointment_set" | "sold" | "already_customer";

export interface CanvassingPin {
  id: string;
  latitude: number;
  longitude: number;
  address: string | null;
  homeowner_name: string | null;
  outcome: PinOutcome;
  follow_up_date: string | null;
  notes: string | null;
  lead_id: string | null;
  knocked_at: string;
  session_id: string | null;
  territory_id: string | null;
  user_id: string;
}

export interface CanvassingTerritory {
  id: string;
  name: string;
  description: string | null;
  center_lat: number | null;
  center_lng: number | null;
  radius_miles: number;
  storm_triggered: boolean;
  storm_date: string | null;
  status: string;
  assigned_to: string | null;
  coverage_percent: number;
  total_doors: number;
  doors_knocked: number;
  appointments_set: number;
}

export interface CanvassingSession {
  id: string;
  territory_id: string | null;
  user_id: string;
  started_at: string;
  ended_at: string | null;
  status: string;
  doors_knocked: number;
  appointments_set: number;
  leads_created: number;
  gps_breadcrumbs: any;
  notes: string | null;
}

export function useCanvassing() {
  const { user, orgId } = useAuth();
  const [territories, setTerritories] = useState<CanvassingTerritory[]>([]);
  const [sessions, setSessions] = useState<CanvassingSession[]>([]);
  const [pins, setPins] = useState<CanvassingPin[]>([]);
  const [activeSession, setActiveSession] = useState<CanvassingSession | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [terr, sess, pinData] = await Promise.all([
        supabase.from("canvassing_territories").select("*").eq("org_id", orgId).order("created_at", { ascending: false }),
        supabase.from("canvassing_sessions").select("*").eq("org_id", orgId).order("started_at", { ascending: false }),
        supabase.from("canvassing_pins").select("*").eq("org_id", orgId).order("knocked_at", { ascending: false }),
      ]);
      if (terr.data) setTerritories(terr.data as CanvassingTerritory[]);
      if (sess.data) {
        const sessData = sess.data as CanvassingSession[];
        setSessions(sessData);
        const active = sessData.find(s => s.status === "active" && s.user_id === user?.id);
        setActiveSession(active || null);
      }
      if (pinData.data) setPins(pinData.data as CanvassingPin[]);
    } catch (err) {
      console.error("Error fetching canvassing data:", err);
    } finally {
      setLoading(false);
    }
  }, [orgId, user?.id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const startSession = async (territoryId?: string) => {
    if (!orgId || !user?.id) return null;
    const { data, error } = await supabase.from("canvassing_sessions").insert({
      org_id: orgId,
      user_id: user.id,
      territory_id: territoryId || null,
    }).select().single();
    if (error) { toast.error("Failed to start session"); return null; }
    setActiveSession(data as CanvassingSession);
    toast.success("Canvassing session started!");
    return data;
  };

  const endSession = async () => {
    if (!activeSession) return;
    const { error } = await supabase.from("canvassing_sessions")
      .update({ status: "completed", ended_at: new Date().toISOString() })
      .eq("id", activeSession.id);
    if (error) { toast.error("Failed to end session"); return; }
    setActiveSession(null);
    toast.success("Session completed!");
    fetchAll();
  };

  const addPin = async (pin: {
    latitude: number;
    longitude: number;
    address?: string;
    homeowner_name?: string;
    outcome: PinOutcome;
    notes?: string;
    territory_id?: string;
    contact_phone?: string;
  }) => {
    if (!orgId || !user?.id) return null;

    // Phase 3 — auto-create a lead when this is a real opportunity
    let createdLeadId: string | null = null;
    const shouldCreateLead = pin.outcome === "appointment_set" || pin.outcome === "callback" || pin.outcome === "sold";
    if (shouldCreateLead && pin.homeowner_name) {
      const { data: leadData } = await supabase.from("leads").insert({
        org_id: orgId,
        contact_name: pin.homeowner_name,
        contact_phone: pin.contact_phone || null,
        property_address: pin.address || null,
        source: "Door Knocking",
        lead_source: "canvassing",
        status: pin.outcome === "sold" ? "won" : "qualified",
        lead_temperature: pin.outcome === "sold" ? "hot" : pin.outcome === "appointment_set" ? "warm" : "cold",
        notes: pin.notes || `Created from canvassing — outcome: ${pin.outcome}`,
        created_by: user.id,
      }).select("id").single();
      if (leadData) createdLeadId = leadData.id;
    }

    const { data, error } = await supabase.from("canvassing_pins").insert({
      org_id: orgId,
      user_id: user.id,
      session_id: activeSession?.id || null,
      territory_id: pin.territory_id || activeSession?.territory_id || null,
      latitude: pin.latitude,
      longitude: pin.longitude,
      address: pin.address || null,
      homeowner_name: pin.homeowner_name || null,
      outcome: pin.outcome,
      notes: pin.notes || null,
      lead_id: createdLeadId,
    }).select().single();
    if (error) { toast.error("Failed to add pin"); return null; }
    setPins(prev => [data as CanvassingPin, ...prev]);

    // Phase 3 — auto-schedule re-knock task 2 days out for "not_home"
    if (pin.outcome === "not_home" && pin.address) {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 2);
      await supabase.from("tasks").insert({
        org_id: orgId,
        title: `Re-knock: ${pin.address}`,
        description: `Homeowner not home on first attempt${pin.homeowner_name ? ` (${pin.homeowner_name})` : ""}.`,
        category: "canvassing",
        priority: "medium",
        due_date: dueDate.toISOString(),
        created_by: user.id,
        assigned_to: user.id,
      });
    }

    // Update session counters
    if (activeSession) {
      const newKnocked = activeSession.doors_knocked + 1;
      const newAppts = pin.outcome === "appointment_set" ? activeSession.appointments_set + 1 : activeSession.appointments_set;
      const newLeads = createdLeadId ? activeSession.leads_created + 1 : activeSession.leads_created;
      await supabase.from("canvassing_sessions").update({
        doors_knocked: newKnocked,
        appointments_set: newAppts,
        leads_created: newLeads,
      }).eq("id", activeSession.id);
      setActiveSession(prev => prev ? { ...prev, doors_knocked: newKnocked, appointments_set: newAppts, leads_created: newLeads } : null);
    }

    if (createdLeadId) {
      toast.success("Lead created from door knock!");
    }
    return data;
  };

  const updatePinOutcome = async (pinId: string, outcome: PinOutcome, notes?: string) => {
    const { error } = await supabase.from("canvassing_pins").update({ outcome, notes: notes || undefined }).eq("id", pinId);
    if (error) { toast.error("Failed to update"); return; }
    setPins(prev => prev.map(p => p.id === pinId ? { ...p, outcome, notes: notes || p.notes } : p));
  };

  const deletePin = async (pinId: string) => {
    const { error } = await supabase.from("canvassing_pins").delete().eq("id", pinId);
    if (error) { toast.error("Failed to remove pin"); return; }
    setPins(prev => prev.filter(p => p.id !== pinId));
  };

  const createTerritory = async (territory: {
    name: string;
    description?: string;
    center_lat: number;
    center_lng: number;
    radius_miles?: number;
    storm_triggered?: boolean;
    storm_date?: string;
  }) => {
    if (!orgId) return null;
    const { data, error } = await supabase.from("canvassing_territories").insert({
      org_id: orgId,
      ...territory,
    }).select().single();
    if (error) { toast.error("Failed to create territory"); return null; }
    setTerritories(prev => [data as CanvassingTerritory, ...prev]);
    toast.success(`Territory "${territory.name}" created!`);
    return data;
  };

  const addBreadcrumb = async (lat: number, lng: number) => {
    if (!activeSession) return;
    const crumbs = [...(activeSession.gps_breadcrumbs || []), { lat, lng, ts: Date.now() }];
    await supabase.from("canvassing_sessions").update({ gps_breadcrumbs: crumbs }).eq("id", activeSession.id);
    setActiveSession(prev => prev ? { ...prev, gps_breadcrumbs: crumbs } : null);
  };

  return {
    territories, sessions, pins, activeSession, loading,
    startSession, endSession, addPin, updatePinOutcome, deletePin,
    createTerritory, addBreadcrumb, refetch: fetchAll,
  };
}
